import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { useSite } from "../context/SiteContext";
import { resolveMedia } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { SectionLabel } from "../components/SectionLabel";

const DEFAULT_IMG_1 = "https://images.unsplash.com/photo-1764265928226-4ee4e583e9b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const DEFAULT_IMG_2 = "https://images.unsplash.com/photo-1764265930595-5e5bc68aaae3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const Body = ({ text }) => (
  <div className="mt-6 space-y-4 max-w-xl">
    {(text || "").split("\n\n").filter(Boolean).map((p, i) =>
      p.startsWith("[Editable") ? (
        <p key={i} className="text-xs font-mono tracking-[0.2em] uppercase text-gold/60">{p}</p>
      ) : (
        <p key={i} className="text-sand leading-relaxed">{p}</p>
      )
    )}
  </div>
);

const Chapter = ({ num, title, body, flip = false, image }) => (
  <section className="py-16 sm:py-24 border-t border-gold/10">
    <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center`}>
      <div className={`lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
        <Reveal>
          <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-gold/70 mb-4">{num}</p>
          <h2 className="font-serif text-3xl sm:text-5xl tracking-tight text-cream" data-testid={`about-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            {title}
          </h2>
          <Body text={body} />
        </Reveal>
      </div>
      {image && (
        <Reveal className={`lg:col-span-5 ${flip ? "lg:order-1" : ""}`} delay={0.15}>
          <div className="overflow-hidden border border-gold/20">
            <img src={resolveMedia(image)} alt={title} loading="lazy" className="w-full aspect-[4/5] object-cover hover:scale-105 transition-transform duration-700" />
          </div>
        </Reveal>
      )}
    </div>
  </section>
);

const PullQuote = ({ children }) => (
  <Reveal className="py-14 sm:py-20 text-center">
    <blockquote className="font-serif italic text-2xl sm:text-4xl text-goldglow/90 leading-snug max-w-3xl mx-auto" data-testid="about-pull-quote">
      "{children}"
    </blockquote>
  </Reveal>
);

export default function About() {
  const site = useSite();
  useSEO("About", "Queen Denae — Southern soul artist, storyteller, and creator of The Royal Vault. The music is only one part of the world.");

  return (
    <div data-testid="about-magazine-profile" className="pt-32 sm:pt-40 pb-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <SectionLabel>The Story</SectionLabel>
        <Reveal>
          <h1 className="font-serif text-5xl sm:text-7xl tracking-tight text-cream" data-testid="about-heading">
            MEET<br /><span className="italic text-goldglow">QUEEN DENAE</span>
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-cream/85 leading-relaxed max-w-2xl">
            Queen Denae makes music for grown women who have lived enough life to know exactly who they are — or are finally giving themselves permission to find out.
          </p>
        </Reveal>

        <PullQuote>{site.about_quote_1 || "Soul. Story. Style. No permission required."}</PullQuote>

        <Chapter num="Chapter 01" title="WHO IS QUEEN DENAE" image={site.about_image_1 || DEFAULT_IMG_1} body={site.about_ch1_body || ""} />

        <Chapter num="Chapter 02" title="THE MUSIC" flip image={site.about_image_2 || DEFAULT_IMG_2} body={site.about_ch2_body || ""} />

        <PullQuote>{site.about_quote_2 || "The music is only one part of the world."}</PullQuote>

        <Chapter num="Chapter 03" title="THE WOMAN BEHIND THE MUSIC" body={site.about_ch3_body || ""} image={site.about_image_3 || ""} />

        <Chapter num="Chapter 04" title="THE NEXT CHAPTER" body={site.about_ch4_body || ""} />

        <Reveal className="mt-16 text-center border border-gold/20 bg-gallery p-10 sm:p-16">
          <p className="font-serif text-3xl sm:text-4xl text-cream tracking-tight">Come inside the world.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/vault" data-testid="about-vault-btn" className="h-14 px-9 inline-flex items-center gap-3 bg-gold text-obsidian font-mono text-[11px] tracking-[0.3em] uppercase hover:bg-goldglow transition-colors">
              Enter the Vault <ArrowRight size={14} />
            </Link>
            <Link to="/royal-list" data-testid="about-royal-list-btn" className="h-14 px-9 inline-flex items-center border border-cream/40 text-cream font-mono text-[11px] tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors">
              Join the Royal List
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
